import { trackGoogleAnalyticsEvent } from "./googleAnalytics";

var CMS_ANALYTICS_BASE =
  (process.env.REACT_APP_CMS_API_BASE_URL ||
    "https://cms-api.manabakh.ir/v1/cms/api") + "/analytics";
var RT_ANALYTICS_BASE = (process.env.REACT_APP_RT_EVENT_API_URL || "https://rt-event.manabakh.ir") + "/v1/rt-event/api/analytics";
var ANONYMOUS_ID_KEY = "mnb_anonymous_id";
var IMPRESSION_FLUSH_MS = 15000;
var pendingImpressions = [];
var impressionFlushTimer = null;

function getToken() {
  try {
    return localStorage.getItem("webTvUserToken");
  } catch (e) {
    return null;
  }
}

function getAppVersion() {
  return process.env.REACT_APP_VERSION || process.env.NEXT_PUBLIC_APP_VERSION || "";
}

export function createAnalyticsId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (char) {
    var random = Math.random() * 16 | 0;
    var value = char === "x" ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
}

export function getOrCreateAnonymousId() {
  try {
    var existing = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (existing) return existing;

    var id = createAnalyticsId();
    localStorage.setItem(ANONYMOUS_ID_KEY, id);
    return id;
  } catch (e) {
    return createAnalyticsId();
  }
}

function getHeaders(token) {
  var headers = {
    "Content-Type": "application/json",
    "X-Device": "webtv",
    "X-App-Version": getAppVersion(),
  };

  if (token) {
    headers.Authorization = "Bearer " + token;
  }

  return headers;
}

function postJson(url, payload, options) {
  var token = options && options.token !== undefined ? options.token : getToken();
  if (!token) return Promise.resolve(null);

  return fetch(url, {
    method: "POST",
    headers: getHeaders(token),
    body: JSON.stringify(payload),
    keepalive: Boolean(options && options.keepalive),
  }).catch(function (error) {
    console.warn("[MediaAnalytics] Request failed:", error);
  });
}

export function normalizeContentKind(content) {
  var kind = content?.contentType?.kind || content?.kind?.kind || content?.kind || (content?.isLive ? "live" : "movie");
  return kind === "sport" ? "program" : kind;
}

export function getContentMode(content) {
  return content?.isLive === 1 || content?.isLive === true || normalizeContentKind(content) === "live" ? "live" : "vod";
}

export function sendContentView(params) {
  if (!params || !params.contentKey) return Promise.resolve();

  trackGoogleAnalyticsEvent("content_view", {
    content_id: params.contentKey,
    content_type: params.contentKind || "movie",
    content_mode: params.mode || "vod",
    surface: params.surface || "content_detail",
    source: params.source || (document.referrer ? "referrer" : "direct"),
  });

  var token = params.token || getToken();
  if (!token) return Promise.resolve(null);

  return postJson(CMS_ANALYTICS_BASE + "/content-view", {
    eventId: createAnalyticsId(),
    anonymousId: getOrCreateAnonymousId(),
    contentKey: params.contentKey,
    mode: params.mode || "vod",
    contentKind: params.contentKind || "movie",
    surface: params.surface || "content_detail",
    source: params.source || (document.referrer ? "referrer" : "direct"),
    timestamp: Date.now(),
  }, { token: token });
}

export function sendPlayIntent(params) {
  if (!params || !params.contentKey) return Promise.resolve();

  trackGoogleAnalyticsEvent("play_intent", {
    content_id: params.contentKey,
    content_type: params.contentKind || "movie",
    content_mode: params.mode || "vod",
    surface: params.surface || "content_detail",
    source: params.source || "primary_play_button",
  });

  var token = params.token || getToken();
  if (!token) return Promise.resolve(null);

  return postJson(CMS_ANALYTICS_BASE + "/play-intent", {
    eventId: createAnalyticsId(),
    anonymousId: getOrCreateAnonymousId(),
    contentKey: params.contentKey,
    mode: params.mode || "vod",
    contentKind: params.contentKind || "movie",
    surface: params.surface || "content_detail",
    source: params.source || "primary_play_button",
    timestamp: Date.now(),
  }, { token: token });
}

export function queueContentImpression(params) {
  if (!params || !params.contentKey) return;

  trackGoogleAnalyticsEvent("content_impressions", {
    content_id: params.contentKey,
    content_type: params.contentKind || "movie",
    content_mode: params.mode || "vod",
    surface: params.surface || "webtv",
    source: params.source || "content_row",
    container_key: params.containerKey || "",
    position: params.position || 0,
  });

  if (!getToken()) return;

  pendingImpressions.push({
    eventId: createAnalyticsId(),
    anonymousId: getOrCreateAnonymousId(),
    contentKey: params.contentKey,
    mode: params.mode || "vod",
    contentKind: params.contentKind || "movie",
    surface: params.surface || "webtv",
    source: params.source || "content_row",
    containerKey: params.containerKey || "",
    position: params.position || 0,
    visibleMs: params.visibleMs || 1000,
    timestamp: Date.now(),
  });

  scheduleImpressionFlush();
}

export function flushContentImpressions(options) {
  if (!pendingImpressions.length) return Promise.resolve();
  var token = options && options.token !== undefined ? options.token : getToken();
  if (!token) return Promise.resolve(null);
  if (impressionFlushTimer && typeof window !== "undefined") {
    window.clearTimeout(impressionFlushTimer);
    impressionFlushTimer = null;
  }

  var events = pendingImpressions.splice(0, 100);
  return postJson(CMS_ANALYTICS_BASE + "/content-impressions", {
    events: events,
  }, {
    token: token,
    keepalive: options && options.keepalive,
  });
}

function scheduleImpressionFlush() {
  if (impressionFlushTimer || typeof window === "undefined") return;

  impressionFlushTimer = window.setTimeout(function () {
    impressionFlushTimer = null;
    flushContentImpressions();
  }, IMPRESSION_FLUSH_MS);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", function () {
    flushContentImpressions({ keepalive: true });
  });
}

export function createPlaybackSession() {
  return createAnalyticsId();
}

export function sendMediaEvent(event, options) {
  if (!event || !event.playbackSessionId || !event.contentKey || !event.eventType) return Promise.resolve();

  trackGoogleAnalyticsEvent("media_event", {
    event_type: event.eventType,
    content_id: event.contentKey,
    content_type: event.contentKind,
    content_mode: event.mode,
    playback_session_id: event.playbackSessionId,
    position_sec: event.positionSec,
    duration_sec: event.durationSec,
    player: event.player,
    cdn: event.cdn,
  });

  return postJson(RT_ANALYTICS_BASE + "/media-event", Object.assign({
    eventId: createAnalyticsId(),
    anonymousId: getOrCreateAnonymousId(),
    timestamp: Date.now(),
  }, event), {
    token: options && options.token,
    keepalive: options && options.keepalive,
  });
}

export function sendSessionEnd(event) {
  if (!event || !event.playbackSessionId || !event.contentKey) return;

  trackGoogleAnalyticsEvent("media_event", {
    event_type: "session_end",
    content_id: event.contentKey,
    content_type: event.contentKind,
    content_mode: event.mode,
    playback_session_id: event.playbackSessionId,
    position_sec: event.positionSec,
    duration_sec: event.durationSec,
    player: event.player,
    cdn: event.cdn,
  });

  var body = JSON.stringify(Object.assign({
    eventId: createAnalyticsId(),
    anonymousId: getOrCreateAnonymousId(),
    eventType: "session_end",
    timestamp: Date.now(),
  }, event));
  var token = getToken();

  if (!token) return null;

  return postJson(RT_ANALYTICS_BASE + "/media-event", JSON.parse(body), {
    token: token,
    keepalive: true,
  });
}
